/**
 * „An: Vespera | Schmied“ — Martins Empfänger-Wahl im Dreier-Modus. (16.10.2026, Stufe 1, shared/dreier-adressat.ts)
 *
 * Martin, 16.10. 07:13: Ja zu „Stufe 1“. Statt dass ein Satzmuster rät, ob er den Schmied meint („Frage dezidiert an Schmidt …“
 * ging an Vespera), wählt er es über dem Eingabefeld. Ist „Schmied“ gewählt, reist mit der NÄCHSTEN Nachricht — getippt (Chat)
 * oder gesprochen (Sprechtaste) — das Feld `an: "schmied"`; der Server ruft Vespera dann nicht (server/dreier-modus.ts,
 * `nachrichtAnSchmied`) und meldet `schmied`, das Mikro wartet auf ihn (SCHMIED_ANGESPROCHEN, lib/schmied-warten.ts).
 *
 * Regeln (EIN Besitzer):
 *   - sichtbar und wirksam NUR bei „Entwickler im Chat“ (Konto 1, Schalter an) — sonst gilt immer Vespera, und es reist kein Feld;
 *   - „Vespera“ ist die Vorgabe und schickt KEIN Feld: die Anrede im Text („Hey Schmied …“) gilt dann weiter wie bisher;
 *   - die Wahl gilt für EINE Nachricht: hat der Server sie angenommen, fällt sie auf „Vespera“ zurück. Kam sie nicht an
 *     (offline, abgewiesen), bleibt sie stehen — das erneute Senden geht wieder an den Schmied.
 *   - 16.10.2026 (Faden zum Schmied, server/dreier-modus.ts): der Faden bleibt am Server beim Schmied, bis Martin Vespera
 *     ausdrücklich dazuholt. Tippt er „Vespera“ an (auch wenn es schon steht — die Vorgabe schickt kein Feld), reist mit der
 *     NÄCHSTEN Nachricht `an: "vespera"`: der Faden endet, Vespera antwortet. Auch das gilt für eine Nachricht.
 *   - 27.10.2026 (Martin 15:22: „warum macht Vespera nicht weiter …“): ein gewählter Entwickler-Chip klebt nicht — wird er
 *     WAHL_VERFALL_MS lang nicht mit einer Nachricht verbraucht, springt die Anzeige von selbst auf „Vespera“ zurück, und es
 *     reist kein Feld mehr (auch wenn der Zeitgeber im Hintergrund-Tab gedrosselt war: geprüft wird bei jedem Lesen).
 *     Solange eine Aufnahme läuft oder Martin tippt (`frische`, lib/ptt.ts und app.ts), beginnt die Frist neu.
 */
import { entwicklerHauptAn } from "../../shared/entwickler-im-chat.js";
import type { MartinsEmpfaenger } from "../../shared/dreier-adressat.js";
import { ZUSATZ_IDS, entwickler, istZusatzId, type ZusatzId } from "../../shared/entwickler.js";
import { loadSettings } from "./settings.js";

/** So lange bleibt ein gewählter Entwickler-Chip ohne Nachricht stehen, dann fällt er auf „Vespera“ zurück. */
export const WAHL_VERFALL_MS = 10 * 60_000;

/** Das Ereignis, wenn sich die Wahl ändert (die Anzeige zeichnet neu). */
export const EMPFAENGER_GEAENDERT = "voice-v8:empfaenger-geaendert";

export interface EmpfaengerWahl {
  /** Was gerade gilt — „vespera“, wenn der Dreier-Modus aus ist. */
  empfaenger(): MartinsEmpfaenger;
  /** Wählen. Bei ausgeschaltetem Dreier-Modus bleibt es bei Vespera. */
  waehle(e: MartinsEmpfaenger): void;
  /**
   * Das Feld `an` für die nächste Nachricht — nur im Dreier-Modus: „schmied“ bei dieser Wahl, „vespera“ nach ausdrücklichem
   * Antippen von Vespera, sonst nichts (der Server entscheidet aus Anrede und Faden).
   */
  anFeld(): MartinsEmpfaenger | undefined;
  /** Die Nachricht mit diesem Feld hat der Server angenommen — die Wahl fällt auf die Vorgabe (Vespera, ohne Feld) zurück. */
  verbraucht(an: MartinsEmpfaenger | undefined): void;
  /**
   * 27.10.2026 (Prüfer): Martin ist dabei, die Nachricht zu machen (Aufnahme läuft, er tippt) — ein gewählter Entwickler-Chip verfällt
   * dann nicht; WAHL_VERFALL_MS beginnt neu. Ohne Entwickler-Chip: nichts.
   */
  frische(): void;
  /** Ist der Dreier-Modus an (Wahl sichtbar)? */
  sichtbar(): boolean;
  /** Gibt es die Wahl „Zimmermann“ (sein Kontoschalter ist an)? */
  zimmermannDa(): boolean;
  /** 17.10.2026: gibt es die Wahl dieses Zusatz-Entwicklers (Zimmermann, Hüter — sein Kontoschalter ist an)? */
  zusatzDa(id: ZusatzId): boolean;
}

export function neueEmpfaengerWahl(
  dreierAn: () => boolean,
  gemeldet: () => void = () => undefined,
  /**
   * 17.10.2026: ist der Kontoschalter dieses Zusatz-Entwicklers an („Zimmermann im Chat“, „Hüter im Chat“)? Nur dann gibt es seine
   * Wahl (ohne Angabe: keiner).
   */
  zusatzAn: (id: ZusatzId) => boolean = () => false,
): EmpfaengerWahl {
  let gewaehlt: MartinsEmpfaenger = "vespera";
  /** Wann der Entwickler-Chip gewählt wurde (für WAHL_VERFALL_MS). */
  let gewaehltSeit = 0;
  let wecker: ReturnType<typeof setTimeout> | null = null;
  /** Hat Martin Vespera ausdrücklich angetippt (im Dreier-Modus)? Dann reist `an: "vespera"` mit der nächsten Nachricht. */
  let vesperaGeholt = false;
  const an = (): boolean => {
    try {
      return dreierAn();
    } catch {
      return false;
    }
  };
  const da = (id: ZusatzId): boolean => {
    try {
      return an() && zusatzAn(id) === true;
    } catch {
      return false;
    }
  };
  const stelleWecker = (): void => {
    if (wecker !== null) clearTimeout(wecker);
    wecker = null;
    if (gewaehlt === "vespera") return;
    gewaehltSeit = Date.now();
    wecker = setTimeout(verfalle, WAHL_VERFALL_MS);
  };
  const setze = (e: MartinsEmpfaenger): void => {
    const neu = gewaehlt !== e;
    gewaehlt = e;
    stelleWecker();
    if (!neu) return;
    gemeldet();
  };
  /** Ein Entwickler-Chip älter als WAHL_VERFALL_MS fällt auf Vespera zurück (die Anzeige zeichnet über `gemeldet` neu). */
  function verfalle(): void {
    if (gewaehlt !== "vespera" && Date.now() - gewaehltSeit >= WAHL_VERFALL_MS) setze("vespera");
  }
  return {
    // Die Wahl eines Zusatz-Entwicklers gilt nur, solange sein Schalter an ist — sonst fällt sie auf Vespera (kein Feld unterwegs).
    empfaenger: () => {
      verfalle();
      return !an() ? "vespera" : istZusatzId(gewaehlt) && !da(gewaehlt) ? "vespera" : gewaehlt;
    },
    waehle: (e) => {
      const dreier = an();
      vesperaGeholt = e === "vespera" && dreier;
      setze(e === "schmied" && dreier ? "schmied" : istZusatzId(e) && da(e) ? e : "vespera");
    },
    anFeld: () => {
      verfalle();
      if (!an()) return undefined;
      if (istZusatzId(gewaehlt)) return da(gewaehlt) ? gewaehlt : undefined;
      return gewaehlt === "schmied" ? "schmied" : vesperaGeholt ? "vespera" : undefined;
    },
    verbraucht: (feld) => {
      if (feld === "vespera") vesperaGeholt = false;
      if (feld === "schmied" || istZusatzId(feld)) setze("vespera");
    },
    frische: () => {
      verfalle(); // schon abgelaufen (gedrosselter Wecker): nicht wiederbeleben
      if (gewaehlt !== "vespera") stelleWecker();
    },
    zimmermannDa: () => da("zimmermann"),
    zusatzDa: da,
    sichtbar: an,
  };
}

/**
 * EIN Weg für Text, Bild und Datei (app.ts: Eingabefeld, `attachImageToChat`, `attachFileToChat`): liest das Feld `an` der Wahl,
 * reicht es dem Senden mit und verbraucht die Wahl erst, wenn das Senden `angenommen` meldet (Server hat mit 200 angenommen).
 * Kam die Nachricht nicht an, bleibt die Wahl stehen. Vorher ging ein Bild oder eine Datei an Vespera vorbei am Chip „Schmied“
 * (18.10.2026, Martin): diese Wege kannten das Feld nicht. Der Beleg-Weg (botMessage) bleibt bewusst bei Vespera, ohne `an`.
 */
export async function sendeMitWahl<T>(
  wahl: Pick<EmpfaengerWahl, "anFeld" | "verbraucht">,
  sende: (an: MartinsEmpfaenger | undefined, angenommen: () => void) => Promise<T>,
): Promise<T> {
  const anFeld = wahl.anFeld();
  return sende(anFeld, () => wahl.verbraucht(anFeld));
}

/** Die Wahl der Seite — Chat (app.ts) und Sprechtaste (lib/ptt.ts) lesen dieselbe. */
export const empfaengerWahl = neueEmpfaengerWahl(
  () => entwicklerHauptAn(loadSettings() as unknown as Record<string, unknown>, (window as unknown as { __voiceV8Uid?: number }).__voiceV8Uid),
  () => {
    try {
      window.dispatchEvent(new CustomEvent(EMPFAENGER_GEAENDERT));
    } catch {
      /* kein window (Tests) */
    }
  },
  (id) => {
    const feld = entwickler(id).kontoFeld;
    return feld !== null && (loadSettings() as unknown as Record<string, unknown>)[feld] === true;
  },
);

const NAMEN: Readonly<Record<MartinsEmpfaenger, string>> = {
  vespera: "Vespera",
  schmied: "Schmied",
  zimmermann: entwickler("zimmermann").name,
  hueter: entwickler("hueter").name,
};
/**
 * 20.10.2026 (Martin 08:18: beim Sprechen mit dem Schmied stand kurz „Vespera“ in der Warteblase, dann „Schmied“): der Name über
 * der Warteblase, SOLANGE die Nachricht unterwegs ist. Die Weiche entscheidet am Server (Anrede, Faden, KI-Weiche) — vorher
 * ist der Empfänger nur bekannt, wenn die Wahl `an` mitschickt. Regeln:
 *   - Dreier-Modus aus (Konto 2 …): wie bisher der Name des Bots;
 *   - `an` gesetzt: genau dieser Empfänger (Vespera → Bot-Name);
 *   - sonst offen: KEIN Name (neutral) — nie „Vespera“ vor der Entscheidung. Rein.
 */
export function wartenderName(wahl: Pick<EmpfaengerWahl, "sichtbar">, an: MartinsEmpfaenger | undefined, botName: string | null): string | null {
  if (!wahl.sichtbar()) return botName;
  if (!an) return null;
  return an === "vespera" ? botName : NAMEN[an];
}

const WAHL_REIHE: readonly MartinsEmpfaenger[] = ["vespera", "schmied", ...ZUSATZ_IDS];

/** Scrollt die quer scrollende Reihe nur so weit, dass `knopf` ganz zu sehen ist (nie die Seite). */
function holeInDieReihe(reihe: HTMLElement, knopf: HTMLElement): void {
  const r = reihe.getBoundingClientRect();
  const k = knopf.getBoundingClientRect();
  // Rechts liegt ein ausgeblendeter Rand (style.css, 16 px) — der Knopf soll davor stehen.
  const rand = 16;
  if (k.right > r.right - rand) reihe.scrollLeft += k.right - (r.right - rand);
  else if (k.left < r.left) reihe.scrollLeft -= r.left - k.left;
}

/**
 * Die Anzeige: „An: [Vespera] [Schmied] ([Zimmermann] [Hüter], je nur mit seinem Schalter)“ als Radiogruppe (Pfeiltasten wechseln, Tab springt hinein und hinaus; aria-checked).
 * Aussehen in style.css (.empfaenger-wahl). `zeichne` gleicht Sichtbarkeit und Stand ab — bei jeder Änderung der Wahl oder der
 * Einstellungen rufen.
 */
export function baueEmpfaengerWahl(wahl: EmpfaengerWahl): { el: HTMLElement; zeichne: () => void } {
  const el = document.createElement("div");
  el.className = "empfaenger-wahl";
  el.setAttribute("role", "radiogroup");
  el.setAttribute("aria-labelledby", "empfaenger-wahl-titel");
  const titel = document.createElement("span");
  titel.className = "empfaenger-wahl-titel";
  titel.id = "empfaenger-wahl-titel";
  titel.textContent = "An:";
  el.appendChild(titel);
  const titelText = document.createElement("span");
  titelText.className = "empfaenger-wahl-vorleser";
  titelText.textContent = " Empfänger der nächsten Nachricht";
  titel.appendChild(titelText);

  const knoepfe = WAHL_REIHE.map((e) => {
    const b = document.createElement("button");
    b.type = "button";
    b.className = "empfaenger-wahl-knopf";
    b.dataset.empfaenger = e;
    b.setAttribute("role", "radio");
    b.textContent = NAMEN[e];
    b.title =
      e === "schmied"
        ? "Die nächste Nachricht geht an den Schmied — Vespera antwortet nicht"
        : istZusatzId(e)
          ? `Die nächste Nachricht geht an den ${NAMEN[e]} — Vespera antwortet nicht`
          : "Die nächste Nachricht geht an Vespera";
    b.addEventListener("click", () => {
      wahl.waehle(e);
      zeichne();
    });
    el.appendChild(b);
    return b;
  });

  el.addEventListener("keydown", (ev: KeyboardEvent) => {
    if (!["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End"].includes(ev.key)) return;
    ev.preventDefault();
    const jetzt = wahl.empfaenger();
    // Nur die sichtbaren Wahlen (Zimmermann und Hüter nur mit ihrem Schalter); die Pfeile laufen im Kreis.
    const reihe = WAHL_REIHE.filter((e) => !istZusatzId(e) || wahl.zusatzDa(e));
    const i = Math.max(0, reihe.indexOf(jetzt));
    const schritt = ev.key === "ArrowLeft" || ev.key === "ArrowUp" ? -1 : 1;
    const ziel: MartinsEmpfaenger =
      ev.key === "Home" ? reihe[0] : ev.key === "End" ? reihe[reihe.length - 1] : reihe[(i + schritt + reihe.length) % reihe.length];
    wahl.waehle(ziel);
    zeichne();
    knoepfe.find((b) => b.dataset.empfaenger === wahl.empfaenger())?.focus();
  });

  function zeichne(): void {
    const sichtbar = wahl.sichtbar();
    el.hidden = !sichtbar;
    // Geht der Dreier-Modus aus, vergisst die Wahl den Schmied — beim Wiedereinschalten steht sie auf Vespera.
    if (!sichtbar) wahl.waehle("vespera");
    const jetzt = wahl.empfaenger();
    el.dataset.empfaenger = jetzt;
    for (const b of knoepfe) {
      const e = b.dataset.empfaenger;
      b.hidden = istZusatzId(e) && !wahl.zusatzDa(e);
      const gewaehlt = b.dataset.empfaenger === jetzt;
      b.setAttribute("aria-checked", gewaehlt ? "true" : "false");
      // Roving tabindex: nur die gewählte ist per Tab erreichbar, die Pfeile wechseln.
      b.tabIndex = gewaehlt ? 0 : -1;
    }
    // 22.10.2026: die Reihe steht in der Werkzeugreihe und scrollt quer (style.css) — die gewählte bleibt im Blick.
    const gewaehlterKnopf = knoepfe.find((b) => b.dataset.empfaenger === jetzt);
    if (sichtbar && gewaehlterKnopf) holeInDieReihe(el, gewaehlterKnopf);
  }
  zeichne();
  return { el, zeichne };
}
